// @ts-nocheck
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const AI_AUTOCOMPLETE_KEY = new PluginKey("aiAutocomplete");

const dispatchAIEvent = (type: string) => {
  window.dispatchEvent(new CustomEvent("ai-autocomplete", { detail: { type } }));
};

export const AIAutocomplete = Extension.create({
  name: "aiAutocomplete",

  addProseMirrorPlugins() {
    let lastTwoChars = "";
    let isGenerating = false;
    let aiInsertedRange: { from: number; to: number } | null = null;

    return [
      new Plugin({
        key: AI_AUTOCOMPLETE_KEY,
        props: {
          handleClick(view) {
            if (aiInsertedRange) {
              // Remove ai-highlight mark from the inserted range
              const mark = view.state.schema.marks["ai-highlight"];
              if (mark) {
                const { from, to } = aiInsertedRange;
                const docSize = view.state.doc.content.size;
                const safeTo = Math.min(to, docSize);
                if (from < safeTo) {
                  const tr = view.state.tr.removeMark(from, safeTo, mark);
                  view.dispatch(tr);
                }
              }
              aiInsertedRange = null;
            }
            return false;
          },

          handleTextInput(view, from, to, text) {
            // Typing also clears highlight
            if (aiInsertedRange) {
              const mark = view.state.schema.marks["ai-highlight"];
              if (mark) {
                const { from: hFrom, to: hTo } = aiInsertedRange;
                const docSize = view.state.doc.content.size;
                const safeTo = Math.min(hTo, docSize);
                if (hFrom < safeTo) {
                  const tr = view.state.tr.removeMark(hFrom, safeTo, mark);
                  view.dispatch(tr);
                }
              }
              aiInsertedRange = null;
            }

            if (isGenerating) return false;

            lastTwoChars = lastTwoChars.slice(-1) + text;

            if (lastTwoChars === "++") {
              // Delete the "++"
              const tr = view.state.tr.delete(from - 1, to);
              view.dispatch(tr);
              lastTwoChars = "";
              isGenerating = true;
              dispatchAIEvent("loading");

              // Gather context
              const doc = view.state.doc;
              let contextText = "";
              const cursorPos = from - 1;
              doc.nodesBetween(0, cursorPos, (node) => {
                if (node.isText) {
                  contextText += node.text;
                } else if (node.isBlock && contextText.length > 0) {
                  contextText += "\n";
                }
              });

              const prompt = contextText.slice(-500);

              fetch("/api/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ prompt, option: "continue" }),
              })
                .then(async (response) => {
                  if (!response.ok || !response.body) {
                    isGenerating = false;
                    dispatchAIEvent("done");
                    return;
                  }

                  const reader = response.body.getReader();
                  const decoder = new TextDecoder();
                  let fullText = "";

                  while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split("\n").filter(Boolean);
                    for (const line of lines) {
                      if (line.startsWith("0:")) {
                        try {
                          const parsed = JSON.parse(line.slice(2));
                          if (typeof parsed === "string") fullText += parsed;
                        } catch { /* skip */ }
                      }
                    }
                  }

                  if (fullText.length > 0) {
                    const cleanText = fullText
                      .replace(/^#+\s/gm, "")
                      .replace(/\*\*/g, "")
                      .replace(/\*/g, "")
                      .replace(/^[-*]\s/gm, "")
                      .replace(/^\d+\.\s/gm, "")
                      .replace(/```[\s\S]*?```/g, "")
                      .replace(/\n{2,}/g, " ")
                      .trim();

                    if (cleanText.length > 0) {
                      const insertPos = view.state.selection.from;
                      const textBefore = view.state.doc.textBetween(
                        Math.max(0, insertPos - 1), insertPos
                      );
                      const prefix = textBefore && textBefore !== " " && textBefore !== "\n" ? " " : "";
                      const finalText = prefix + cleanText;

                      // Insert text + apply ai-highlight in one transaction
                      const aiMark = view.state.schema.marks["ai-highlight"];
                      if (aiMark) {
                        const mark = aiMark.create({ color: "rgba(37, 99, 235, 0.25)" }); // visible blue highlight
                        const insertTr = view.state.tr
                          .insertText(finalText, insertPos)
                          .addMark(insertPos, insertPos + finalText.length, mark);
                        view.dispatch(insertTr);
                        aiInsertedRange = { from: insertPos, to: insertPos + finalText.length };
                      } else {
                        // Fallback without highlight
                        const insertTr = view.state.tr.insertText(finalText, insertPos);
                        view.dispatch(insertTr);
                      }
                    }
                  }

                  isGenerating = false;
                  dispatchAIEvent("done");
                })
                .catch((err) => {
                  console.error("AI autocomplete error:", err);
                  isGenerating = false;
                  dispatchAIEvent("done");
                });

              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
